import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

function Welcome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const source = searchParams.get('source');

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }

    // Set token in axios headers for this session
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      // Complete registration
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/marketplace/complete-registration`,
        {
          email: formData.email,
          password: formData.password,
          name: formData.name
        }
      );

      // Set new token
      const newToken = response.data.data.token;
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // Redirect to pricing
      navigate('/pricing');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '600px', padding: '60px 20px' }}>
      <div className="card" style={{ textAlign: 'center' }}>
        {/* Welcome Header */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: '#007bff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            🎉
          </div>
          <h1 style={{ fontSize: '32px', marginBottom: '12px' }}>
            Welcome to ViRaffle!
          </h1>
          <p style={{ fontSize: '18px', color: '#666' }}>
            {source === 'marketplace'
              ? "You've successfully installed our app from GoHighLevel!"
              : "Let's get your account set up"}
          </p>
        </div>

        {/* Progress Steps */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: step >= 1 ? '#007bff' : '#e0e0e0',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              1
            </div>
            <div style={{
              width: '60px',
              height: '3px',
              background: step >= 2 ? '#007bff' : '#e0e0e0'
            }} />
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: step >= 2 ? '#007bff' : '#e0e0e0',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              2
            </div>
            <div style={{
              width: '60px',
              height: '3px',
              background: step >= 3 ? '#007bff' : '#e0e0e0'
            }} />
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: step >= 3 ? '#007bff' : '#e0e0e0',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              3
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px', color: '#666' }}>
          <small>
            Step {step} of 3: {
              step === 1 ? 'Account Details' :
              step === 2 ? 'Review' :
              'Choose Plan'
            }
          </small>
        </div>

        {/* Registration Form */}
        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} style={{ textAlign: 'left' }}>
            <label>Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
            />

            <label>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              required
            />

            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Min. 8 characters"
              required
            />

            <label>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
              required
            />

            <button type="submit" style={{ marginTop: '16px', width: '100%' }}>
              Continue →
            </button>
          </form>
        )}

        {/* Review Step */}
        {step === 2 && (
          <div style={{ textAlign: 'left' }}>
            <h3 style={{ marginBottom: '24px' }}>Review Your Information</h3>

            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '24px' }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Name:</strong> {formData.name}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Email:</strong> {formData.email}
              </div>
              <div>
                <strong>Password:</strong> •••••••• (hidden)
              </div>
            </div>

            {error && (
              <div className="error" style={{ marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  background: '#6c757d'
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? 'Creating Account...' : 'Complete Setup →'}
              </button>
            </div>
          </div>
        )}

        {/* Benefits Section */}
        <div style={{
          marginTop: '40px',
          padding: '24px',
          background: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'left'
        }}>
          <h4 style={{ marginBottom: '16px' }}>What's Next?</h4>
          <ul style={{ paddingLeft: '20px', color: '#666' }}>
            <li style={{ marginBottom: '8px' }}>Choose your perfect plan (starting at $0/month)</li>
            <li style={{ marginBottom: '8px' }}>Create your first digital raffle</li>
            <li style={{ marginBottom: '8px' }}>Start collecting entries from your GHL contacts</li>
            <li>Let our system automatically select winners!</li>
          </ul>
        </div>

        {/* Support */}
        <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
          Need help? <a href="mailto:support@your-domain.com" style={{ color: '#007bff' }}>Contact Support</a>
        </div>
      </div>
    </div>
  );
}

export default Welcome;
